/**
 * bl-header.js — Global Commerce Shell behaviour.
 *
 * Registered components (see BrightLeaf.components):
 *   header             sticky + transparent behaviour, header height var
 *   navigation         desktop dropdown / mega menu, full keyboard support
 *   announcement-bar   optional auto-rotation, pause on hover/focus
 *
 * Mobile navigation uses the shared bl-disclosure primitive (bl-disclosure.js).
 *
 * Everything degrades gracefully: with JS disabled the header is a static
 * bar, navigation renders as plain links, and the announcement bar shows
 * its first message.
 */

const BL = window.BrightLeaf;
const DESKTOP = '(min-width: 990px)';

/* ------------------------------------------------------------------ header */

BL.components.register('header', (root, { utils, events }) => {
  const sticky = root.dataset.sticky === 'true';
  const transparent = root.classList.contains('bl-header--transparent');
  const sentinel = document.querySelector('[data-bl-header-sentinel]');

  const setHeight = () => {
    document.documentElement.style.setProperty('--bl-header-height', `${root.offsetHeight}px`);
  };
  setHeight();

  const observer = new ResizeObserver(utils.throttle(setHeight, 100));
  observer.observe(root);

  let io;
  if ((sticky || transparent) && sentinel && 'IntersectionObserver' in window) {
    io = new IntersectionObserver(
      ([entry]) => {
        const scrolled = !entry.isIntersecting;
        root.classList.toggle('bl-header--scrolled', scrolled);
        if (transparent) root.classList.toggle('bl-header--solid', scrolled);
        events.emit('bl:header:scrolled', { scrolled });
      },
      { rootMargin: '0px' },
    );
    io.observe(sentinel);
  }

  return () => {
    observer.disconnect();
    io?.disconnect();
  };
});

/* -------------------------------------------------------------- navigation */

BL.components.register('navigation', (root, { utils }) => {
  const items = [...root.querySelectorAll('[data-bl-nav-item][data-bl-has-panel]')];
  if (!items.length) return undefined;

  let openItem = null;
  let closeTimer;
  let isDesktop = false;

  const panelOf = (item) => item.querySelector('[data-bl-nav-panel]');
  const triggerOf = (item) => item.querySelector('[data-bl-nav-trigger]');

  function open(item) {
    if (openItem && openItem !== item) close(openItem);
    window.clearTimeout(closeTimer);
    item.classList.add('is-open');
    triggerOf(item)?.setAttribute('aria-expanded', 'true');
    panelOf(item)?.removeAttribute('hidden');
    openItem = item;
  }

  function close(item = openItem) {
    if (!item) return;
    item.classList.remove('is-open');
    triggerOf(item)?.setAttribute('aria-expanded', 'false');
    panelOf(item)?.setAttribute('hidden', '');
    if (openItem === item) openItem = null;
  }

  function closeAll() {
    items.forEach(close);
  }

  /* Pointer: intent-delayed close so diagonal travel into the panel works. */
  items.forEach((item) => {
    item.addEventListener('pointerenter', () => {
      if (!isDesktop) return;
      window.clearTimeout(closeTimer);
      open(item);
    });
    item.addEventListener('pointerleave', () => {
      if (!isDesktop) return;
      closeTimer = window.setTimeout(() => close(item), 180);
    });

    /* Keyboard / touch: the trigger is a real button that toggles. */
    triggerOf(item)?.addEventListener('click', (event) => {
      event.preventDefault();
      if (item.classList.contains('is-open')) close(item);
      else open(item);
    });

    /* Focus leaving the item entirely closes it. */
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) close(item);
    });

    item.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && item.classList.contains('is-open')) {
        event.stopPropagation();
        close(item);
        triggerOf(item)?.focus();
      }
      if (event.key === 'ArrowDown' && event.target === triggerOf(item)) {
        event.preventDefault();
        open(item);
        utils.focusable(panelOf(item))[0]?.focus();
      }
    });
  });

  /* Roving left/right along the top level. */
  root.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const triggers = [...root.querySelectorAll('[data-bl-nav-link]')];
    const index = triggers.indexOf(document.activeElement);
    if (index === -1) return;
    event.preventDefault();
    const next = event.key === 'ArrowRight' ? index + 1 : index - 1;
    triggers[(next + triggers.length) % triggers.length].focus();
  });

  const onDocClick = (event) => {
    if (!root.contains(event.target)) closeAll();
  };
  document.addEventListener('click', onDocClick);

  const stopMedia = utils.media(DESKTOP, (matches) => {
    isDesktop = matches;
    closeAll();
  });

  return () => {
    document.removeEventListener('click', onDocClick);
    stopMedia();
  };
});

/* -------------------------------------------------- announcement rotation */

BL.components.register('announcement-bar', (root, { utils, events }) => {
  const slides = [...root.querySelectorAll('[data-bl-announcement]')];
  const interval = Number(root.dataset.rotate || 0) * 1000;
  if (slides.length < 2 || !interval || utils.prefersReducedMotion()) return undefined;

  let index = 0;
  let timer = null;

  const show = (next) => {
    slides[index].hidden = true;
    index = (next + slides.length) % slides.length;
    slides[index].hidden = false;
    events.emit('bl:announcement:changed', { index });
  };

  const start = () => {
    if (timer) return;
    timer = window.setInterval(() => show(index + 1), interval);
  };
  const stop = () => {
    window.clearInterval(timer);
    timer = null;
  };

  root.addEventListener('pointerenter', stop);
  root.addEventListener('pointerleave', start);
  root.addEventListener('focusin', stop);
  root.addEventListener('focusout', start);
  document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

  root.querySelector('[data-bl-announcement-prev]')?.addEventListener('click', () => show(index - 1));
  root.querySelector('[data-bl-announcement-next]')?.addEventListener('click', () => show(index + 1));

  start();
  return stop;
});
